/*
 This is handrolled code. This service is intentional meant to be a singleton within a angular application.
*/

import { combineLatest, filter, map, pluck } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  AccountsApiService,
  ActivationHistoryItem,
  DeactivationReasonSummary,
  GetAppActivationStatusForBusinessRequest,
  GetDeactivationReasonsForAppRequest,
  GetDeactivationReasonsForAppRequestGetDeactivationReasonsForAppFilterOptions as GetDeactivationReasonsForAppFilterOptions,
  ListActivationEventHistoryForBusinessRequest,
  ListCustomDeactivationReasonsForAppRequest,
  ListCustomDeactivationReasonsForAppRequestListCustomDeactivationReasonsForAppFilterOptions as ListCustomDeactivationReasonsForAppFilterOptions,
  ListCustomDeactivationReasonsForAppResponse,
  ListDeactivatedBusinessIDsForAppRequest,
  ListDeactivatedBusinessIDsForAppResponse,
} from '@vendasta/accounts/legacy';

@Injectable()
export class ActivationEventService {
  public request: ListDeactivatedBusinessIDsForAppRequest;
  public allDeactivatedBusinessIds$$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  public deactivatedBusinessIdsSlice$$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  public deactivatedBusinessIdsSliceRequest$$: BehaviorSubject<number> = new BehaviorSubject(0);
  private loadingDeactivatedBusinessIds$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private hasMoreDeactivatedBusinessIds$$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor(private accountsApiService: AccountsApiService) {
    this.request = new ListDeactivatedBusinessIDsForAppRequest();
    this.setupDeactivatedBusinessIdsSliceSubscription();
  }

  get loadingDeactivatedBusinessIds$(): Observable<boolean> {
    return this.loadingDeactivatedBusinessIds$$.asObservable();
  }

  get allDeactivatedBusinessIds$(): Observable<string[]> {
    return this.allDeactivatedBusinessIds$$.asObservable();
  }

  get hasMoreDeactivatedBusinessIds$(): Observable<boolean> {
    return this.hasMoreDeactivatedBusinessIds$$.asObservable();
  }

  get deactivatedBusinessIdsSlice$(): Observable<string[]> {
    return this.deactivatedBusinessIdsSlice$$.asObservable();
  }

  set deactivatedBusinessIdsSliceRequest(value: number) {
    this.deactivatedBusinessIdsSliceRequest$$.next(value);
  }

  public isAccountActive$(appId: string, accountId: string): Observable<boolean> {
    const request = new GetAppActivationStatusForBusinessRequest();
    request.businessId = accountId;
    request.appId = appId;
    const response = this.accountsApiService.getAppActivationStatusForBusiness(request);
    return response.pipe(pluck('status'), pluck('currentlyActive'));
  }

  setupDeactivatedBusinessIdsSliceSubscription(): void {
    this.deactivatedBusinessIdsSliceRequest$$
      .pipe(
        combineLatest(this.allDeactivatedBusinessIds$),
        map(([deactivatedBusinessIdsSliceLength, ids]) => {
          if (ids) {
            if (ids.length <= deactivatedBusinessIdsSliceLength) {
              this.hasMoreDeactivatedBusinessIds$$.next(false);
            } else {
              this.hasMoreDeactivatedBusinessIds$$.next(true);
            }
            return ids.length <= deactivatedBusinessIdsSliceLength
              ? ids.slice(0, ids.length)
              : ids.slice(0, deactivatedBusinessIdsSliceLength);
          } else {
            return [];
          }
        }),
      )
      .subscribe((value) => {
        this.deactivatedBusinessIdsSlice$$.next(value);
      });
  }

  getAllDeactivatedBusinessIds(appId: string, partnerId?: string): void {
    const request = new ListDeactivatedBusinessIDsForAppRequest();
    request.appId = appId;

    this.allDeactivatedBusinessIds$$.next([]);

    if (partnerId) {
      const filterOptions = new ListCustomDeactivationReasonsForAppFilterOptions();
      filterOptions.partnerId = partnerId;
      request.filterOptions = filterOptions;
    }

    this.accountsApiService.listDeactivatedBusinessIDsForApp(request).subscribe(
      (resp: ListDeactivatedBusinessIDsForAppResponse) => this.updateDeactivatedBusinessIdsSubjects(resp),
      (err) => this.updateDeactivatedBusinessIdsSubjectsOnError(err),
    );
  }

  updateDeactivatedBusinessIdsSubjects(response: ListDeactivatedBusinessIDsForAppResponse): void {
    this.loadingDeactivatedBusinessIds$$.next(false);
    this.allDeactivatedBusinessIds$$.next(response.businessIds);
  }

  updateDeactivatedBusinessIdsSubjectsOnError(err: any): void {
    this.loadingDeactivatedBusinessIds$$.next(false);
    this.allDeactivatedBusinessIds$$.error(err);
  }

  previewCustomDeactivationReasons(
    appId: string,
    previewSize: number,
    partnerId?: string,
  ): Observable<DeactivationReasonSummary[]> {
    const request = new ListCustomDeactivationReasonsForAppRequest();

    if (partnerId) {
      const filterOptions = new ListCustomDeactivationReasonsForAppFilterOptions();
      filterOptions.partnerId = partnerId;
      request.filterOptions = filterOptions;
    }

    request.appId = appId;
    // Cursor at 0 in a base64 string
    request.cursor = '==MA';
    request.pageSize = previewSize;

    return this.accountsApiService
      .listCustomDeactivationReasonsForApp(request)
      .pipe(map((resp: ListCustomDeactivationReasonsForAppResponse) => resp.deactivationReasonSummary));
  }

  getDeactivationReasons(appId: string, partnerId?: string): Observable<DeactivationReasonSummary[]> {
    const request = new GetDeactivationReasonsForAppRequest({ appId: appId });

    if (partnerId) {
      const filterOptions = new GetDeactivationReasonsForAppFilterOptions();
      filterOptions.partnerId = partnerId;
      request.filterOptions = filterOptions;
    }

    return this.accountsApiService
      .getDeactivationReasonsForApp(request)
      .pipe(map((resp) => resp.deactivationReasonSummary));
  }

  listActivationEventHistory(
    businessId: string,
    appId: string,
    cursor?: string,
    pageSize?: number,
  ): Observable<ActivationHistoryItem[]> {
    const request = new ListActivationEventHistoryForBusinessRequest();
    request.businessId = businessId;
    request.cursor = cursor;
    request.pageSize = pageSize;

    const response = this.accountsApiService.listActivationEventHistoryForBusiness(request);
    // There is no endpoint scoped to both businessId and appId so I am filtering it here in the meantime
    return response.pipe(
      pluck('activationHistoryItems'),
      filter((items: ActivationHistoryItem[]) => Boolean(items) && items.length > 0),
      map((items: ActivationHistoryItem[]) => items.filter((item) => item.itemId === appId)),
    );
  }
}
